import { ref, computed, nextTick } from 'vue';
import { promptChainApi } from '@/services/promptChainApi';
import { isDocumentResult } from '@/utils/documentResultValidator';
import axios from 'axios';

export function useChainExecution(config) {
  // config: { promptChain, node, doc, store, loginState } — each a getter function or ref

  const self = {
    resolvedInputs: ref(null),
    executionResults: ref([]),
    selectedResult: ref(null),
    triggerAction: ref(null),
    resolvingInputs: ref(false),
    previousExecutions: ref([]),
    hasPreviousExecutions: ref(false),
    mappingErrorDetails: ref(null),
    showMappingError: ref(false),
    regeneratingPartial: ref(false),
    executingBulk: ref(false),
    loadingPreviousExecution: ref(false),
    skipActiveGenerationResume: ref(false),

    // Execution config state (for sequence mode support)
    executionMode: ref('parallel'),     // 'parallel' | 'sequence'
    contextMode: ref('summary'),        // 'summary' | 'full'
    maxContextRows: ref(20),
    contextOutputKeys: ref([]),
    sessionExecutionId: ref(null),
    sessionPollTimer: ref(null),
  };

  const polledSessionExecutionIds = new Set();

  // Helper to get config values
  function getPromptChain() {
    return typeof config.promptChain === 'function' ? config.promptChain() : config.promptChain?.value;
  }
  function getNode() {
    return typeof config.node === 'function' ? config.node() : config.node?.value;
  }
  function getDoc() {
    return typeof config.doc === 'function' ? config.doc() : config.doc?.value;
  }
  function getStore() {
    return typeof config.store === 'function' ? config.store() : config.store;
  }

  // --- Computed ---
  self.manualInputRequirements = computed(() => {
    if (!self.resolvedInputs.value) return [];

    const manualInputs = [];

    self.resolvedInputs.value.steps?.forEach(step => {
      step.inputs?.forEach(input => {
        const isStaticInput = input.source === 'static' && !input.value;
        const isFailedNonFrameworkInput = input.error &&
                                          !input.error.startsWith('LEVEL_FRAMEWORK') &&
                                          input.source !== 'level_framework';

        if (isStaticInput || isFailedNonFrameworkInput) {
          manualInputs.push({
            step_name: step.step_name,
            property: input.property,
            metadata: input.metadata || {},
            error: input.error || null,
            source: input.source
          });
        }
      });
    });

    return manualInputs;
  });

  self.levelFrameworkRequirements = computed(() => {
    if (!self.resolvedInputs.value) return [];

    const requirements = {};

    self.resolvedInputs.value.steps?.forEach(step => {
      step.inputs?.forEach(input => {
        if (input.error && typeof input.error === 'string' &&
            (input.error.startsWith('LEVEL_FRAMEWORK') || input.error.startsWith('TRACK_FRAMEWORK'))) {
          const errorParts = input.error.split(':');
          const htId = errorParts[1];
          const errorType = errorParts[0];
          const isTrackError = errorType.startsWith('TRACK_');

          if (!requirements[htId]) {
            requirements[htId] = {
              ht_id: /^\d+$/.test(htId) ? parseInt(htId) : htId,
              error_type: errorType,
              message: errorParts.slice(2).join(':'),
              inputs: [],
              levelSelection: input.levelSelection || [],
              trackSelection: input.trackSelection || [],
              needsTrackSelection: false,
              isTrackOnly: false
            };
          }

          // Merge selections from subsequent inputs (first input may not carry all data)
          if (input.levelSelection?.length > 0 && requirements[htId].levelSelection.length === 0) {
            requirements[htId].levelSelection = input.levelSelection;
          }

          if (isTrackError) {
            requirements[htId].needsTrackSelection = true;
            if (input.trackSelection?.length > 0) {
              requirements[htId].trackSelection = input.trackSelection;
            }
          }

          // Also flag track selection needed when a level error carries trackSelection data
          if (!isTrackError && input.trackSelection?.length > 0) {
            requirements[htId].needsTrackSelection = true;
            requirements[htId].trackSelection = input.trackSelection;
          }

          requirements[htId].inputs.push({
            step_name: step.step_name,
            property: input.property,
            source_field: input.sourceField
          });
        }
      });
    });

    const result = Object.values(requirements);
    result.forEach(req => {
      req.isTrackOnly = req.levelSelection.length === 0 && req.trackSelection.length > 0;
    });
    return result;
  });

  self.hasManualInputs = computed(() => {
    return self.manualInputRequirements.value.length > 0 || self.levelFrameworkRequirements.value.length > 0;
  });

  self.chainOutputKeys = computed(() => {
    if (!self.resolvedInputs.value?.steps) return [];
    const keys = [];
    const seen = new Set();
    for (const step of self.resolvedInputs.value.steps) {
      const stepName = step.step_name || `Step ${step.step_order || '?'}`;
      for (const output of (step.outputs || [])) {
        if (output.property && !seen.has(output.property)) {
          seen.add(output.property);
          keys.push({
            text: `${stepName} → ${output.property}`,
            value: output.property
          });
        }
      }
    }
    return keys;
  });

  self.getStepsForOutput = computed(() => {
    return (result) => {
      if (!result || !result.executionDetails || !self.resolvedInputs.value) {
        return [];
      }

      const outputStepMap = {};

      self.resolvedInputs.value.steps?.forEach(step => {
        step.outputs?.forEach(output => {
          if (!outputStepMap[output.property]) {
            outputStepMap[output.property] = [];
          }
          outputStepMap[output.property].push({
            step_name: step.step_name,
            step_order: step.step_order || 0
          });
        });
      });

      return outputStepMap;
    };
  });

  self.availableOutputsForRegeneration = computed(() => {
    return (result) => {
      if (!result || !result.success) return [];

      const outputs = [];

      if (isDocumentResult(result)) {
        const documentData = result.result.populatedData || [];

        const partTypes = new Set();
        documentData.forEach(item => {
          partTypes.add(item.type);
        });

        Array.from(partTypes).forEach(partType => {
          outputs.push({
            key: partType,
            label: partType,
            type: 'document_part',
            steps: []
          });
        });
      } else {
        const stepMap = self.getStepsForOutput.value(result);

        if (result.result) {
          outputs.push({
            key: 'result',
            label: 'Main Result',
            type: 'output_property',
            steps: stepMap['result'] || []
          });
        }

        if (result.reason) {
          outputs.push({
            key: 'reason',
            label: 'Reason',
            type: 'output_property',
            steps: stepMap['reason'] || []
          });
        }

        if (result.additionalOutputs) {
          Object.keys(result.additionalOutputs).forEach(key => {
            outputs.push({
              key: key,
              label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
              type: 'output_property',
              steps: stepMap[key] || []
            });
          });
        }
      }

      return outputs;
    };
  });

  self.getHierarchyOptions = (htId) => {
    const store = getStore();
    const hierarchies = store?.state?.hierarchies?.hierarchies || [];
    const hierarchy = hierarchies.find(h => h.ht_id === htId);

    if (!hierarchy || !hierarchy.values) {
      return [];
    }

    return hierarchy.values.map(item => ({
      text: item.text || item.value,
      value: item.value,
      node_id: item.node_id
    }));
  };

  // --- Private helpers ---

  function _buildResolvePayload(promptChainId, skipPreviousCheck = false) {
    const node = getNode();
    const doc = getDoc();
    const promptChain = getPromptChain();

    const payload = {
      prompt_chain_id: promptChainId,
      node: node ? node.node : null,
      doc: doc || null,
      tmpl_id: node ? node.tmpl_id : null
    };

    if (skipPreviousCheck) {
      payload.skip_previous_check = true;
    }

    if (promptChain?.trigger_id) {
      payload.trigger_id = promptChain.trigger_id;
      payload.trigger_type = promptChain.trigger_type;
      let triggerContext = promptChain.trigger_config;
      switch (promptChain.trigger_type) {
        case "hierarchy_node_action":
          triggerContext = {
            ...triggerContext,
            node_ids: [node.node.hierarchy_node_id],
            tmpl_id: node.tmpl_id
          };
          break;
        case "document_action":
          triggerContext = {
            ...triggerContext,
            ...doc
          };
          break;
      }
      payload.trigger_context = triggerContext;
    }

    return payload;
  }

  function _processResolveResponse(responseData) {
    if (responseData.has_previous_executions && responseData.previous_executions.length > 0) {
      self.hasPreviousExecutions.value = true;
      self.previousExecutions.value = responseData.previous_executions;
      return { nextState: 'EXECUTION_SELECTION' };
    }

    self.resolvedInputs.value = responseData.data;
    self.triggerAction.value = responseData.data.trigger_context;

    // Check for LEVEL_FRAMEWORK_NO_MAPPING errors
    let hasMappingError = false;
    const errorDetails = {
      steps: [],
      hierarchies: new Set()
    };

    responseData.data.steps?.forEach(step => {
      step.inputs?.forEach(input => {
        if (input.error && typeof input.error === 'string' && input.error.startsWith('LEVEL_FRAMEWORK_NO_MAPPING')) {
          hasMappingError = true;
          const errorParts = input.error.split(':');
          const htId = errorParts[1];
          const unmappedValue = errorParts.slice(2).join(':');

          errorDetails.hierarchies.add(htId);
          errorDetails.steps.push({
            step_name: step.step_name,
            property: input.property,
            ht_id: htId,
            unmapped_value: unmappedValue
          });
        }
      });
    });

    if (hasMappingError) {
      self.showMappingError.value = true;
      self.mappingErrorDetails.value = {
        steps: errorDetails.steps,
        hierarchies: Array.from(errorDetails.hierarchies)
      };
      return { nextState: 'MAPPING_ERROR' };
    }

    // Initialize execution config from chain defaults
    initExecutionConfig(responseData.data);

    return { nextState: 'INPUT_FORM' };
  }

  function initExecutionConfig(resolveData) {
    const execConfig = resolveData?.execution_config;
    if (execConfig) {
      if (execConfig.context_mode) self.contextMode.value = execConfig.context_mode;
      if (execConfig.max_context_rows !== undefined) self.maxContextRows.value = execConfig.max_context_rows;
    }

    // Pre-select outputs flagged as context in step configs
    if (resolveData?.steps) {
      const flaggedKeys = [];
      for (const step of resolveData.steps) {
        for (const output of (step.outputs || [])) {
          if (output.isContextOutput && output.property) {
            flaggedKeys.push(output.property);
          }
        }
      }
      if (flaggedKeys.length > 0) {
        self.contextOutputKeys.value = flaggedKeys;
      }
    }
  }

  // --- Public methods ---

  self.resolveInputs = async (promptChainId, skipPreviousCheck, notify) => {
    if (!promptChainId) {
      if (notify) notify('Missing chain ID', 'error');
      return { nextState: null };
    }

    try {
      self.resolvingInputs.value = true;
      self.resolvedInputs.value = null;
      self.showMappingError.value = false;
      self.mappingErrorDetails.value = null;

      const payload = _buildResolvePayload(promptChainId, skipPreviousCheck);
      const response = await axios.post('/externalContent/resolvePromptChainInputs', payload);

      if (response.data && response.data.success) {
        self.resolvingInputs.value = false;
        const result = _processResolveResponse(response.data);
        if (notify && result.nextState !== 'MAPPING_ERROR' && result.nextState !== 'EXECUTION_SELECTION') {
          notify('Inputs resolved successfully', 'success');
        }
        if (result.nextState === 'MAPPING_ERROR' && notify) {
          notify('Level framework mapping errors detected', 'error');
        }
        return result;
      } else {
        console.error('Failed to resolve inputs:', response.data);
        if (notify) notify('Failed to resolve inputs: ' + (response.data.error || 'Unknown error'), 'error');
        self.resolvingInputs.value = false;
        return { nextState: null };
      }
    } catch (error) {
      console.error('Error resolving inputs:', error);
      if (notify) notify('Error resolving inputs: ' + error.message, 'error');
      self.resolvingInputs.value = false;
      return { nextState: null };
    }
  };

  self.buildManualValues = (result) => {
    const manualValues = { ...(result.inputs?.manual || result.manualInputs || {}) };

    self.levelFrameworkRequirements.value.forEach(req => {
      const selectedValue = result.inputs?.hierarchies?.[req.ht_id] ||
                          result.hierarchySelections?.[req.ht_id];
      if (selectedValue) {
        req.inputs.forEach(input => {
          manualValues[input.property] = selectedValue;
        });
      }

      // Track selection
      const selectedTrack = result.inputs?.trackSelections?.[req.ht_id] ||
                           result.trackSelections?.[req.ht_id];
      if (selectedTrack) {
        req.inputs.forEach(input => {
          manualValues[`${input.property}_track`] = selectedTrack;
        });
      }
    });

    return manualValues;
  };

  self.buildTriggerContext = (manualValues) => {
    const promptChain = getPromptChain();
    const node = getNode();
    const doc = getDoc();

    const baseContext = {
      trigger_type: promptChain.trigger_type,
      trigger_id: promptChain.trigger_id,
      ...promptChain.trigger_config
    };

    switch (promptChain.trigger_type) {
      case "hierarchy_node_action": {
        const nodeId = node?.node?.hierarchy_node_id || node?.hierarchy_node_id;
        const tmplId = node?.tmpl_id ||
                       promptChain?.trigger_config?.tmpl_id ||
                       promptChain?.source_data?.tmpl_id;
        return {
          ...baseContext,
          source_data: {
            node_ids: nodeId ? [nodeId] : [],
            hierarchy_node_id: nodeId,
            tmpl_id: tmplId,
            client_id: promptChain.source_data?.client_id
          }
        };
      }
      case "document_action":
        return {
          ...baseContext,
          source_data: {
            ...doc,
            client_id: promptChain.source_data?.client_id
          }
        };
      default:
        return baseContext;
    }
  };

  self.buildExecutionPayload = (triggerContext, manualValues, options = {}) => {
    const {
      isPartialRegen = false,
      targetOutput = null,
      previousOutputs = null
    } = options;

    const promptChain = getPromptChain();

    const basePayload = {
      prompt_chain_id: promptChain.prompt_chain_id,
      trigger_context: triggerContext
    };

    if (Object.keys(manualValues).length > 0) {
      basePayload.trigger_context = {
        ...basePayload.trigger_context,
        manual_values: manualValues
      };
    }

    if (isPartialRegen) {
      basePayload.partialRegen = {
        target_output: targetOutput,
        previous_outputs: previousOutputs
      };
    }

    return basePayload;
  };

  self.executeChainWithConfig = async (execConfig, polling, notify, emit) => {
    const {
      mode = 'single',
      result = null,
      rows = null,
      isPartialRegen = false,
      targetOutput = null,
      previousOutputs = null
    } = execConfig;

    const promptChain = getPromptChain();

    try {
      if (mode === 'bulk') {
        const promises = rows.map(async (rowResult) => {
          const manualValues = self.buildManualValues(rowResult);
          const triggerContext = self.buildTriggerContext(manualValues);
          const payload = self.buildExecutionPayload(triggerContext, manualValues);

          const resultIndex = self.executionResults.value.findIndex(
            r => r.rowId === rowResult.rowId
          );

          try {
            const response = await promptChainApi.executeChain(payload);

            if (response.data.executionId) {
              const executionId = response.data.executionId;

              if (resultIndex !== -1) {
                self.executionResults.value[resultIndex] = {
                  ...self.executionResults.value[resultIndex],
                  executionId,
                  execution_status: 'running',
                  isProcessing: true
                };
              }

              polling.startPolling(executionId, resultIndex);

              if (emit) {
                emit('executionStarted', {
                  executionId,
                  prompt_chain_id: promptChain.prompt_chain_id,
                  chain_name: promptChain.chain_name
                });
              }
            } else if (response.data.result) {
              const allOutputs = response.data.context?.outputs || {};
              if (resultIndex !== -1) {
                self.executionResults.value[resultIndex].result = allOutputs.result || '';
                self.executionResults.value[resultIndex].success = true;
                self.executionResults.value[resultIndex].processing = false;
              }
            } else if (resultIndex !== -1) {
              self.executionResults.value[resultIndex].success = false;
              self.executionResults.value[resultIndex].processing = false;
              self.executionResults.value[resultIndex].error = response.data.error || 'Execution failed to start';
            }
          } catch (rowError) {
            if (resultIndex !== -1) {
              self.executionResults.value[resultIndex].success = false;
              self.executionResults.value[resultIndex].processing = false;
              self.executionResults.value[resultIndex].error = rowError.message;
            }
          }
        });

        await Promise.all(promises);
        notify('Bulk generation started', 'info');
      } else {
        const manualValues = self.buildManualValues(result);
        const triggerContext = self.buildTriggerContext(manualValues);
        const payload = self.buildExecutionPayload(triggerContext, manualValues, {
          isPartialRegen,
          targetOutput,
          previousOutputs
        });

        result.isProcessing = true;
        result.execution_status = 'queued';

        const response = await promptChainApi.executeChain(payload);

        if (response.data.executionId) {
          const executionId = response.data.executionId;
          result.executionId = executionId;
          result.execution_status = 'running';

          const resultIndex = self.executionResults.value.findIndex(
            r => r.rowId === result.rowId
          );

          polling.startPolling(executionId, resultIndex);

          if (emit) {
            emit('executionStarted', {
              executionId,
              prompt_chain_id: promptChain.prompt_chain_id,
              chain_name: promptChain.chain_name
            });
          }

          const actionType = isPartialRegen ? 'Partial regeneration' : 'Regeneration';
          notify(`${actionType} started (ID: ${executionId})`, 'info');
        } else if (response.data.result) {
          const allOutputs = response.data.context?.outputs || {};
          result.result = allOutputs.result || '';
          result.success = true;
          result.processing = false;

          notify('Generation completed immediately', 'success');
        }

        result.isProcessing = false;
      }
    } catch (error) {
      console.error('Error executing chain:', error);

      if (mode === 'bulk') {
        notify('Error starting bulk generation: ' + error.message, 'error');
      } else {
        result.isProcessing = false;
        result.execution_status = 'error';
        notify('Error starting generation: ' + error.message, 'error');
      }
    }
  };

  self.executeBulkGeneration = async (bulkInput, polling, notify, emit) => {
    if (!bulkInput.canExecuteBulk.value) {
      notify('Please fill in all required fields for each row', 'warning');
      return;
    }

    self.executingBulk.value = true;

    const executionRows = bulkInput.expandRowsToExecutions(bulkInput.bulkRows.value);

    self.executionResults.value = executionRows.map((row, index) => ({
      rowNumber: index + 1,
      rowId: index + 1,
      originalRowId: row.originalRowId,
      result: null,
      inputs: {
        manual: row.manualInputs,
        hierarchies: row.hierarchySelections
      },
      manualInputs: row.manualInputs,
      hierarchySelections: row.hierarchySelections,
      success: null,
      processing: true,
      executionId: null,
      progress: null,
      currentStep: self.executionMode.value === 'sequence' ? 'Queued...' : null
    }));

    self.selectedResult.value = self.executionResults.value[0];

    if (self.executionMode.value === 'sequence') {
      await executeBulkSequence(bulkInput, polling, notify, emit);
    } else {
      await self.executeChainWithConfig({
        mode: 'bulk',
        rows: self.executionResults.value
      }, polling, notify, emit);
    }

    self.executingBulk.value = false;

    const executionCount = self.executionResults.value.length;
    const modeLabel = self.executionMode.value === 'sequence' ? 'sequence' : 'parallel';
    notify(`Started ${executionCount} execution${executionCount > 1 ? 's' : ''} in ${modeLabel} mode.`, 'info');
  };

  async function executeBulkSequence(bulkInput, polling, notify, emit) {
    const promptChain = getPromptChain();

    try {
      const rows = self.executionResults.value.map(rowResult => ({
        manual_values: self.buildManualValues(rowResult)
      }));

      const triggerContext = self.buildTriggerContext({});

      const payload = {
        prompt_chain_id: promptChain.prompt_chain_id,
        trigger_context: triggerContext,
        rows: rows,
        session_config: {
          context_mode: self.contextMode.value,
          max_context_rows: self.maxContextRows.value,
          context_output_keys: self.contextOutputKeys.value.length > 0
            ? self.contextOutputKeys.value
            : undefined
        }
      };

      const response = await promptChainApi.executeBulkSession(payload);

      if (response.data?.success && response.data.sessionExecutionId) {
        self.sessionExecutionId.value = response.data.sessionExecutionId;
        startSessionPolling(polling);

        if (emit) {
          emit('executionStarted', {
            sessionExecutionId: response.data.sessionExecutionId,
            prompt_chain_id: promptChain.prompt_chain_id,
            chain_name: promptChain.chain_name,
            mode: 'sequence'
          });
        }
      } else {
        throw new Error(response.data?.error || 'Failed to start session');
      }
    } catch (error) {
      console.error('Error starting bulk sequence:', error);
      notify('Error starting sequence execution: ' + error.message, 'error');
      self.executionResults.value = self.executionResults.value.map(r => ({
        ...r,
        success: false,
        processing: false,
        error: error.message,
        currentStep: 'Failed to start'
      }));
    }
  }

  // --- Session polling (for sequence mode) ---

  function startSessionPolling(polling) {
    self.stopSessionPolling();
    pollSessionStatus(polling);
    self.sessionPollTimer.value = setInterval(() => pollSessionStatus(polling), 2000);
  }

  self.stopSessionPolling = () => {
    if (self.sessionPollTimer.value) {
      clearInterval(self.sessionPollTimer.value);
      self.sessionPollTimer.value = null;
    }
  };

  async function pollSessionStatus(polling) {
    if (!self.sessionExecutionId.value) return;

    try {
      const resp = await promptChainApi.getExcelSessionStatus(self.sessionExecutionId.value);
      const data = resp.data;

      if (data?.success) {
        const sessionData = data.data;
        const rows = sessionData.rows || [];

        for (const row of rows) {
          const execId = row.iteration_execution_id;
          if (execId && !polledSessionExecutionIds.has(execId)) {
            polledSessionExecutionIds.add(execId);

            const resultIndex = row.iteration_index;
            const existing = self.executionResults.value[resultIndex];

            if (existing) {
              self.executionResults.value = [
                ...self.executionResults.value.slice(0, resultIndex),
                {
                  ...existing,
                  executionId: execId,
                  currentStep: 'Starting...'
                },
                ...self.executionResults.value.slice(resultIndex + 1)
              ];
              polling.startPolling(execId, resultIndex);
            }
          }
        }

        const sessionStatus = sessionData.session?.status;
        if (sessionStatus === 'completed' || sessionStatus === 'failed') {
          self.stopSessionPolling();
        }
      }
    } catch (err) {
      console.error('Session polling error:', err);
    }
  }

  self.regenerateResult = async (result, outputKey, polling, notify, emit) => {
    if (!result || result.processing) return;

    const isPartialRegeneration = outputKey !== null;
    const promptChain = getPromptChain();

    if (isPartialRegeneration) {
      self.regeneratingPartial.value = true;
      result.regeneratingSection = outputKey;
    } else {
      result.processing = true;
      result.success = null;
      result.error = null;
      result.result = null;
      result.additionalOutputs = null;
    }

    if (result.executionId) {
      polling.stopPolling(result.executionId);
    }

    result.progress = null;
    result.currentStep = null;
    result.executionId = null;

    try {
      const manualValues = self.buildManualValues(result);
      const triggerContext = self.buildTriggerContext(manualValues);

      const previousOutputs = isPartialRegeneration ? {
        result: result.result,
        reason: result.reason,
        ...result.additionalOutputs
      } : null;

      const executionPayload = self.buildExecutionPayload(triggerContext, manualValues, {
        isPartialRegen: isPartialRegeneration,
        targetOutput: outputKey,
        previousOutputs
      });

      if (promptChain?.trigger_id) {
        executionPayload.trigger_id = promptChain.trigger_id;
        executionPayload.trigger_type = promptChain.trigger_type;
      } else if (self.resolvedInputs.value?.trigger_context) {
        executionPayload.trigger_type = self.resolvedInputs.value.trigger_context.trigger_type;
        executionPayload.trigger_id = self.resolvedInputs.value.trigger_context.trigger_id;
      }

      const response = await promptChainApi.executeChain(executionPayload);

      if (response.data && response.data.success) {
        const executionId = response.data.executionId;

        if (executionId) {
          result.executionId = executionId;

          const resultIndex = self.executionResults.value.findIndex(r => r.rowId === result.rowId);
          if (resultIndex !== -1) {
            polling.startPolling(executionId, resultIndex);
          }

          if (emit) {
            emit('executionStarted', {
              executionId,
              prompt_chain_id: promptChain.prompt_chain_id,
              chain_name: promptChain.chain_name,
              isRegeneration: true,
              isPartial: isPartialRegeneration
            });
          }

          const regenerationType = isPartialRegeneration ? `${outputKey} section` : 'full chain';
          notify(`Started ${regenerationType} regeneration for row ${result.rowNumber}`, 'info');

          if (isPartialRegeneration) {
            const idx = self.executionResults.value.findIndex(r => r.rowId === result.rowId);
            if (idx !== -1) {
              self.executionResults.value[idx] = { ...self.executionResults.value[idx] };
            }
          }
        } else {
          const allOutputs = response.data.context?.outputs || {};

          if (isPartialRegeneration) {
            if (outputKey === 'result') {
              result.result = allOutputs.result || result.result;
            } else if (outputKey === 'reason') {
              result.reason = allOutputs.reason || result.reason;
            } else {
              result.additionalOutputs = result.additionalOutputs || {};
              result.additionalOutputs[outputKey] = allOutputs[outputKey] || result.additionalOutputs[outputKey];
            }
          } else {
            result.result = allOutputs.result || '';
            result.reason = allOutputs.reason || '';

            const additionalOutputs = {};
            for (const [key, value] of Object.entries(allOutputs)) {
              if (key !== 'result' && key !== 'reason') {
                additionalOutputs[key] = value;
              }
            }
            result.additionalOutputs = additionalOutputs;
          }

          result.success = true;
          result.processing = false;
          result.regeneratingSection = null;

          const regenerationType = isPartialRegeneration ? `${outputKey} section` : 'Row';
          notify(`${regenerationType} ${result.rowNumber} regenerated successfully`, 'success');
        }
      } else {
        result.error = response.data.error || 'Unknown error';
        result.success = false;
        result.processing = false;
        result.regeneratingSection = null;

        notify(`Failed to regenerate row ${result.rowNumber}`, 'error');
      }
    } catch (error) {
      console.error('Error regenerating result:', error);
      result.error = error.message;
      result.success = false;
      result.processing = false;
      result.regeneratingSection = null;

      notify(`Error regenerating row ${result.rowNumber}: ${error.message}`, 'error');
    } finally {
      if (isPartialRegeneration) {
        self.regeneratingPartial.value = false;
      }
    }
  };

  self.resumeActiveGeneration = async (executionId, polling, notify, docSelection) => {
    try {
      self.resolvingInputs.value = false;

      const initialResultEntry = {
        rowId: 1,
        rowNumber: 1,
        processing: true,
        success: null,
        error: null,
        result: null,
        reason: null,
        additionalOutputs: {},
        executionId: executionId,
        chainName: null,
        currentStep: 'Resuming generation...',
        progress: null,
        progressDetails: null,
        awaitingSelection: false,
        selectionData: null,
        regeneratingSection: null,
        executionDetails: null,
        inputs: {
          manual: {},
          hierarchies: {}
        }
      };

      self.executionResults.value = [initialResultEntry];
      self.selectedResult.value = initialResultEntry;

      const response = await axios.get(`/externalContent/execution/${executionId}/status`);

      if (response.data && response.data.success) {
        const execution = response.data.data;
        const resultEntry = self.executionResults.value[0];

        const updates = {
          processing: execution.execution_status === 'running' || execution.execution_status === 'queued',
          success: execution.execution_status === 'completed',
          error: execution.execution_status === 'failed' ? (execution.error_message || 'Execution failed') : null,
          chainName: execution.chain_name,
          awaitingSelection: execution.execution_status === 'awaiting_selection'
        };

        if (execution.execution_status === 'running') {
          if (execution.current_step && execution.progress) {
            const currentStepNum = (execution.progress.completed_steps || 0) + 1;
            updates.currentStep = `${execution.current_step.step_name} (${currentStepNum}/${execution.progress.total_steps || '?'})`;
          } else if (execution.current_step) {
            updates.currentStep = execution.current_step.step_name;
          } else if (execution.status_description) {
            updates.currentStep = execution.status_description;
          } else {
            updates.currentStep = 'Generating content...';
          }
          if (execution.progress) {
            updates.progress = execution.progress.progress_percentage;
            updates.progressDetails = {
              completed: execution.progress.completed_steps,
              total: execution.progress.total_steps,
              failed: execution.progress.failed_steps
            };
          }
        } else if (execution.execution_status === 'queued') {
          updates.currentStep = 'Queued for generation...';
        }

        if (execution.execution_status === 'completed') {
          const allOutputs = {};
          if (execution.steps) {
            execution.steps.forEach(step => {
              if (step.outputs) {
                Object.assign(allOutputs, step.outputs);
              }
            });
          }
          updates.result = allOutputs.result || '';
          updates.reason = allOutputs.reason || '';

          const additionalOutputs = {};
          for (const [key, value] of Object.entries(allOutputs)) {
            if (key !== 'result' && key !== 'reason') {
              additionalOutputs[key] = value;
            }
          }
          updates.additionalOutputs = additionalOutputs;

          updates.executionDetails = {
            chain_name: execution.chain_name,
            total_steps: execution.progress?.total_steps,
            completed_steps: execution.progress?.completed_steps,
            processing_time: execution.completed_at && execution.started_at
              ? new Date(execution.completed_at) - new Date(execution.started_at)
              : null
          };
        }

        if (execution.execution_status === 'awaiting_selection') {
          let selectionData = execution.selection_data;
          if (typeof selectionData === 'string') {
            try {
              selectionData = JSON.parse(selectionData);
            } catch (e) {
              console.error('Failed to parse selection_data:', e);
              selectionData = null;
            }
          }
          updates.selectionData = selectionData;
        }

        const updatedResult = { ...resultEntry, ...updates };
        self.executionResults.value = [updatedResult];
        self.selectedResult.value = updatedResult;

        if (execution.execution_status === 'completed') {
          notify('Active content generation completed', 'success');
          return { status: 'completed', result: updatedResult };
        } else if (execution.execution_status === 'running' || execution.execution_status === 'queued') {
          polling.startPolling(executionId, 0);
          notify('Resuming active content generation...', 'info');
          return { status: 'running' };
        } else if (execution.execution_status === 'awaiting_selection') {
          notify('Content generation awaiting your selection', 'info');
          return { status: 'awaiting_selection', selectionData: updates.selectionData, executionId };
        } else if (execution.execution_status === 'failed') {
          notify(`Content generation failed: ${execution.error_message || 'Unknown error'}`, 'error');
          return { status: 'failed' };
        }
      } else {
        self.executionResults.value = [];
        self.selectedResult.value = null;
        notify('Could not resume active generation, starting fresh', 'warning');
        return { status: 'not_found' };
      }
    } catch (error) {
      console.error('Error resuming active generation:', error);
      self.executionResults.value = [];
      self.selectedResult.value = null;
      notify('Error resuming active generation: ' + error.message, 'error');
      return { status: 'error' };
    }
  };

  self.loadPreviousExecution = async (execution, notify) => {
    self.loadingPreviousExecution.value = true;

    try {
      const response = await axios.get(`/externalContent/promptChainExecutionSteps/${execution.execution_id}?onlyFormatter=true`);

      if (response.data && response.data.success) {
        const transformedResults = self.transformExecutionStepsToResults(response.data.data);

        self.executionResults.value = transformedResults;
        self.selectedResult.value = transformedResults[0];
        self.loadingPreviousExecution.value = false;

        notify('Previous execution loaded successfully', 'success');
        return { success: true, results: transformedResults };
      } else {
        throw new Error(response.data.error || 'Failed to load execution');
      }
    } catch (error) {
      console.error('Error loading previous execution:', error);
      notify('Error loading previous execution: ' + error.message, 'error');
      self.loadingPreviousExecution.value = false;
      return { success: false };
    }
  };

  self.transformExecutionStepsToResults = (steps) => {
    const lastStep = steps.reduce((max, step) => {
      return (!max || step.step_execution_id > max.step_execution_id) ? step : max;
    }, null);

    if (!lastStep || !lastStep.output_data) {
      console.warn('No last step found in execution with output data');
      return [];
    }

    const formatterStep = lastStep;
    const outputs = formatterStep.output_data;

    let resultData = null;
    if (outputs.result && typeof outputs.result === 'object' &&
        outputs.result.populatedData && outputs.result.hierarchies) {
      resultData = outputs.result;
    } else if (outputs.result) {
      try {
        const parsed = typeof outputs.result === 'string' ? JSON.parse(outputs.result) : outputs.result;
        if (parsed && parsed.populatedData && parsed.hierarchies) {
          resultData = parsed;
        } else {
          resultData = outputs.result;
        }
      } catch (e) {
        resultData = outputs.result;
      }
    }

    const result = {
      rowNumber: 1,
      rowId: 1,
      originalRowId: 1,
      result: resultData,
      reason: outputs.reason || null,
      additionalOutputs: {},
      inputs: {
        manual: {},
        hierarchies: {}
      },
      success: true,
      processing: false,
      executionId: formatterStep.execution_id,
      progress: null,
      currentStep: null,
      executionDetails: {
        chain_name: 'Previous Execution',
        total_steps: steps.length,
        completed_steps: steps.filter(s => s.step_status === 'completed').length,
        processing_time: formatterStep.duration
      }
    };

    Object.keys(outputs).forEach(key => {
      if (key !== 'result' && key !== 'reason') {
        result.additionalOutputs[key] = outputs[key];
      }
    });

    steps.forEach(step => {
      if (step.input_data) {
        Object.assign(result.inputs.manual, step.input_data);
      }
    });

    return [result];
  };

  self.checkAndAutoProceed = async (bulkInput, polling, notify, emit) => {
    await nextTick();

    const hasManualRequirements = self.manualInputRequirements.value.length > 0;
    const hasLevelFrameworkReqs = self.levelFrameworkRequirements.value.length > 0;

    if (!hasManualRequirements && !hasLevelFrameworkReqs) {
      const manualValues = {};
      const triggerContext = self.buildTriggerContext(manualValues);
      const payload = self.buildExecutionPayload(triggerContext, manualValues);

      self.executionResults.value = [{
        rowNumber: 1,
        rowId: 1,
        result: null,
        inputs: { manual: {}, hierarchies: {} },
        success: null,
        processing: true,
        executionId: null,
        progress: null,
        currentStep: null
      }];
      self.selectedResult.value = self.executionResults.value[0];

      try {
        const response = await promptChainApi.executeChain(payload);

        if (response.data?.executionId) {
          const executionId = response.data.executionId;
          self.executionResults.value[0] = {
            ...self.executionResults.value[0],
            executionId,
            execution_status: 'running'
          };
          polling.startPolling(executionId, 0);

          if (emit) {
            emit('executionStarted', {
              executionId,
              prompt_chain_id: getPromptChain()?.prompt_chain_id,
              chain_name: getPromptChain()?.chain_name
            });
          }
        } else if (response.data?.result) {
          const allOutputs = response.data.context?.outputs || {};
          self.executionResults.value[0] = {
            ...self.executionResults.value[0],
            result: allOutputs.result || '',
            success: true,
            processing: false
          };
        } else {
          self.executionResults.value[0] = {
            ...self.executionResults.value[0],
            success: false,
            processing: false,
            error: response.data?.error || 'Execution failed to start'
          };
        }
      } catch (error) {
        self.executionResults.value[0] = {
          ...self.executionResults.value[0],
          success: false,
          processing: false,
          error: error.message || 'Error starting execution'
        };
        if (notify) notify('Error starting execution: ' + error.message, 'error');
      }

      return true;
    }

    return false;
  };

  // Navigation helpers
  self.selectResult = (result) => {
    self.selectedResult.value = result;
  };

  self.clearResults = () => {
    self.executionResults.value = [];
    self.selectedResult.value = null;
    self.stopSessionPolling();
    self.sessionExecutionId.value = null;
    polledSessionExecutionIds.clear();
  };

  return self;
}
